import { useEffect, useState } from "react";
import QRCode from "react-qr-code";
import useTranslator from "../../../../hooks/useTranslator";
import { MOBILE_SESSION_STATE } from "../mobileSessionState";

interface ContinueOnPhoneScreenProps {
  qrUrl: string;
  copyUrl: string;
  sessionState: string;
  onCancel: () => void;
}

const isTerminalState = (state: string): boolean =>
  [
    MOBILE_SESSION_STATE.COMPLETED,
    MOBILE_SESSION_STATE.FAILED,
    MOBILE_SESSION_STATE.EXPIRED,
    MOBILE_SESSION_STATE.CANCELLED,
  ].includes(state as any);

const statusLabels: Record<string, string> = {
  [MOBILE_SESSION_STATE.PENDING]: "connectPhoneStatusPending",
  [MOBILE_SESSION_STATE.PHONE_OPENED]: "connectPhoneStatusOpened",
  [MOBILE_SESSION_STATE.FRONT_CAPTURED]: "connectPhoneStatusFront",
  [MOBILE_SESSION_STATE.BACK_CAPTURED]: "connectPhoneStatusBack",
  [MOBILE_SESSION_STATE.SUBMITTED]: "connectPhoneStatusSubmitted",
  [MOBILE_SESSION_STATE.COMPLETED]: "connectPhoneStatusCompleted",
  [MOBILE_SESSION_STATE.FAILED]: "connectPhoneStatusFailed",
  [MOBILE_SESSION_STATE.EXPIRED]: "connectPhoneStatusExpired",
  [MOBILE_SESSION_STATE.CANCELLED]: "connectPhoneStatusCancelled",
};

const getTranslatedText = (
  _t: (key: string, context?: string) => string,
  key: string,
  fallback: string
) => {
  return _t(key);
};

const ContinueOnPhoneScreen = ({
  qrUrl,
  copyUrl,
  sessionState,
  onCancel,
}: ContinueOnPhoneScreenProps) => {
  const { _t } = useTranslator();
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (!isCopied) return;
    const timeout = window.setTimeout(() => setIsCopied(false), 2000);
    return () => window.clearTimeout(timeout);
  }, [isCopied]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(copyUrl);
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = copyUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
    setIsCopied(true);
  };

  const isTerminal = isTerminalState(sessionState);
  const isInProgress =
    !isTerminal && sessionState !== MOBILE_SESSION_STATE.PENDING;

  const statusKey = statusLabels[sessionState] || "connectPhoneStatusPending";
  const statusLabel = _t(statusKey);

  const title = isTerminal
    ? getTranslatedText(_t, "connectPhoneTitleDone", "You're all set")
    : getTranslatedText(_t, "continueOnYourPhone", "Continue on your mobile");

  const description = isTerminal
    ? statusLabel
    : getTranslatedText(
        _t,
        "continueOnYourPhoneDescription",
        "Scan the QR code with your phone camera. When you finish the photos, we'll continue here automatically."
      );

  return (
    <div className="custom-kyc-upload-screen continue-on-phone-screen">
      <div className="continue-on-phone-media">
        {isTerminal ? (
          <div
            className={`continue-on-phone-terminal-icon ${
              sessionState === MOBILE_SESSION_STATE.COMPLETED
                ? "success"
                : "error"
            }`}
          >
            {sessionState === MOBILE_SESSION_STATE.COMPLETED ? "✓" : "✕"}
          </div>
        ) : (
          <div className="continue-on-phone-qr-wrapper">
            <QRCode
              value={qrUrl}
              size={168}
              level="M"
              bgColor="#ffffff"
              fgColor="#253837"
            />
          </div>
        )}
      </div>

      <div className="continue-on-phone-copy">
        <h3>{title}</h3>
        <p>{description}</p>
        {isInProgress && (
          <div
            className="continue-on-phone-status-badge"
            role="status"
            aria-live="polite"
          >
            <div className="continue-on-phone-spinner" aria-hidden="true" />
            <span>{statusLabel}</span>
          </div>
        )}
      </div>

      <div className="custom-kyc-upload-action-panel">
        {!isTerminal && (
          <>
            <button
              type="button"
              className="custom-kyc-upload-primary-btn"
              onClick={handleCopyLink}
            >
              <img src="/images/link-icon.svg" alt="" />
              {isCopied
                ? getTranslatedText(_t, "copied", "Copied")
                : getTranslatedText(_t, "copyLink", "Copy link")}
            </button>
            <button
              type="button"
              className="custom-kyc-upload-secondary-btn continue-on-phone-btn"
              onClick={onCancel}
            >
              {getTranslatedText(
                _t,
                "continueOnComputer",
                "Continue on computer"
              )}
            </button>
          </>
        )}
        {isTerminal && sessionState !== MOBILE_SESSION_STATE.COMPLETED && (
          <button
            type="button"
            className="custom-kyc-upload-primary-btn"
            onClick={onCancel}
          >
            {getTranslatedText(_t, "connectPhoneGoBack", "Go back")}
          </button>
        )}
      </div>
    </div>
  );
};

export default ContinueOnPhoneScreen;
